import React, { useState, useEffect, useRef, useMemo } from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import SelectInput from '../../../components/inputs/select';
import { useSelector } from 'react-redux';

/**
 * Helper to match an account's type against a target filter type.
 * @param {string} accountType - The type of the account from the platform
 * @param {string} filterType - The user-selected account type filter
 * @returns {boolean}
 */
const matchesAccountType = (accountType, filterType) => {
    if (!filterType) return true;
    const t = String(accountType || '').toUpperCase().replace(/[^A-Z]/g, '');
    const f = String(filterType || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (t === f) return true;
    if (f === 'DIRECTCOST' || f === 'DIRECTCOSTS') {
        return t === 'DIRECTCOST' || t === 'DIRECTCOSTS';
    }
    if ((f.startsWith('EXPENSE') || f.startsWith('EXPENSES')) && (t.startsWith('EXPENSE') || t.startsWith('EXPENSES'))) return true;
    if ((f.includes('COST') || f.includes('COGS')) && (t.includes('COST') || t.includes('COGS'))) return true;
    if (f.includes('OTHER') && t.includes('OTHER')) return true;
    return t.startsWith(f) || f.startsWith(t);
};

/**
 * Settings configuration form for accounting platform integration (Xero, QBO, MYOB).
 * Manages default accounts, tax rates, synchronization modes, and status mappings.
 *
 * @param {Object} props
 * @returns {React.ReactElement}
 */
export default function AccountingConfigForm({
    writePermission = true,
    canSyncContacts = true,
    canSyncInvoices = true,
    canSyncBills = true,
    invoiceStatus,
    setInvoiceStatus,
    purchaseOrderStatus,
    setPurchaseOrderStatus,
    squashLinesInvoices,
    setSquashLinesInvoices,
    squashLinesBills,
    setSquashLinesBills,
    squashRetention,
    setSquashRetention,
    lineAmountTypes,
    setLineAmountTypes,
    defaultCurrency,
    setDefaultCurrency,
    accountCode,
    setAccountCode,
    cogsAccountCode,
    setCogsAccountCode,
    contractorAccountCode,
    setContractorAccountCode,
    cogsAccountType,
    setCogsAccountType,
    contractorAccountType,
    setContractorAccountType,
    taxType,
    setTaxType,
    expenseTaxType,
    setExpenseTaxType,
    defaultItemId,
    setDefaultItemId,
    invoiceStatusesList,
    poStatusesList,
    currenciesList,
    accountsList = [],
    taxRatesList,
    itemsList,
    invoiceStatusItems,
    loadingMetadata,
    selectedConnection,
    handleSaveSettings,
    handleSyncContacts,
    handleOpenSyncInvoices,
    handleOpenSyncBills,
    getPlatformDisplayName
}) {
    const accountTypeOptions = useMemo(() => {
        if (!accountsList || !accountsList.length) return [];

        const map = new Map();
        accountsList.forEach(acc => {
            if (acc.type) {
                const val = String(acc.type).toUpperCase();
                if (!map.has(val)) {
                    const label = String(acc.type);
                    map.set(val, { value: val, key: label, label });
                }
            }
        });

        return Array.from(map.values());
    }, [accountsList]);

    const entitlements = useSelector((state) => state.user.entitlements) || {};
    const isPushAllowed = selectedConnection ? (() => {
        if (selectedConnection.platform === 'xero') return entitlements.xeroPush !== false;
        if (selectedConnection.platform === 'quickbooks') return entitlements.qboPush !== false;
        if (selectedConnection.platform === 'myob') return entitlements.myobPush !== false;
        return entitlements.accountingPush !== false;
    })() : true;

    const bottomElementRef = useRef(null);
    const [isBottomVisible, setIsBottomVisible] = useState(true);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsBottomVisible(entry.isIntersecting);
            },
            {
                root: null,
                threshold: 0.1,
            }
        );

        if (bottomElementRef.current) {
            observer.observe(bottomElementRef.current);
        }

        return () => {
            if (bottomElementRef.current) {
                observer.unobserve(bottomElementRef.current);
            }
        };
    }, []);



    return (
        <Col lg={12} className="mb-4">
            <Card className="shadow-sm border mb-3">
                <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <Card.Title as="h2" className="content-heading content-heading-lg text-wrap text-uppercase m-0 brand-heading">
                        Integration Settings - {getPlatformDisplayName(selectedConnection?.platform)} ({selectedConnection?.display_name || selectedConnection?.platform_connection_name})
                    </Card.Title>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {selectedConnection?.is_syncing && (
                            <span className="badge bg-warning text-dark px-3 py-2 d-inline-flex align-items-center">
                                <Spinner size="sm" animation="border" className="me-2" />
                                Syncing in progress...
                            </span>
                        )}
                        {!isBottomVisible && (
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <Button
                                    variant="primary"
                                    className="btn-ch"
                                    onClick={handleSaveSettings}
                                    disabled={!writePermission || selectedConnection?.is_syncing === true}
                                    data-cy="top-save-configuration-btn"
                                >
                                    Save Configuration
                                </Button>
                                <Button
                                    variant="outline-info"
                                    className="btn-ch"
                                    onClick={handleSyncContacts}
                                    disabled={!canSyncContacts || selectedConnection?.is_syncing === true}
                                    data-cy="top-sync-contacts-btn"
                                >
                                    Sync Contacts
                                </Button>
                                <Button
                                    variant="outline-info"
                                    className="btn-ch"
                                    onClick={handleOpenSyncInvoices}
                                    disabled={!canSyncInvoices || selectedConnection?.is_syncing === true || !isPushAllowed}
                                    title={!isPushAllowed ? "Push operations are disabled for this platform under your account's subscription plan" : ""}
                                    data-cy="top-sync-invoices-btn"
                                >
                                    Sync Invoices
                                </Button>
                                <Button
                                    variant="outline-info"
                                    className="btn-ch"
                                    onClick={handleOpenSyncBills}
                                    disabled={!canSyncBills || selectedConnection?.is_syncing === true || !isPushAllowed}
                                    title={!isPushAllowed ? "Push operations are disabled for this platform under your account's subscription plan" : ""}
                                    data-cy="top-sync-bills-btn"
                                >
                                    Sync Bills
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-4 bg-light-subtle">
                    <Form onSubmit={(e) => { e.preventDefault(); if (writePermission) handleSaveSettings(e); }}>
                        {/* Row 1: Top Pair (Left: Card 1 [h-100] | Right: Card 2 + Card 5) */}
                        <Row className="g-4 mb-4">
                            {/* Card 1: General & Default Settings */}
                            <Col xl={6} lg={12}>
                                <Card className="border shadow-sm h-100">
                                    <Card.Header className="bg-white py-3 px-3 border-bottom d-flex align-items-center">
                                        <h6 className="fw-bold text-uppercase m-0 small brand-heading">General & Default Settings</h6>
                                    </Card.Header>
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <Row className="g-3">
                                            <Col xs={12} sm={6}>
                                                <SelectInput
                                                    label="Default Invoice (Progress Claim) Status"
                                                    placeholder="Invoice Status"
                                                    value={invoiceStatus}
                                                    onChange={(e) => setInvoiceStatus(e.target.value)}
                                                    items={invoiceStatusesList.length > 0 ? invoiceStatusesList : invoiceStatusItems}
                                                    disabled={!writePermission}
                                                />
                                                <span className="d-block mt-1 small italic-subtext">
                                                    Initial status when pushing Invoices(Progress Claims).
                                                </span>
                                                {invoiceStatus === 'AUTHORISED' && (
                                                    <div className="mt-1 small brand-color-5 font-weight-500">
                                                        Note: Once pushed as <strong>Awaiting Payment</strong>, invoices cannot be reverted to Draft or Awaiting Approval.
                                                    </div>
                                                )}
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <SelectInput
                                                    label="Default Bill(Vendor Invoices) Status"
                                                    placeholder="PO Status"
                                                    value={purchaseOrderStatus}
                                                    onChange={(e) => setPurchaseOrderStatus(e.target.value)}
                                                    items={poStatusesList.length > 0 ? poStatusesList : invoiceStatusItems}
                                                    disabled={!writePermission}
                                                />
                                                <span className="d-block mt-1 small italic-subtext">
                                                    Initial status when pushing Bill(Vendor Invoices) Orders.
                                                </span>
                                                {purchaseOrderStatus === 'AUTHORISED' && (
                                                    <div className="mt-1 small brand-color-5 font-weight-500">
                                                        Note: Once pushed as <strong>Awaiting Payment</strong>, bills cannot be reverted to Draft or Awaiting Approval.
                                                    </div>
                                                )}
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                {loadingMetadata ? (
                                                    <div className="py-2 text-muted small align-with-inputs">
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Loading currencies...
                                                    </div>
                                                ) : (
                                                    <>
                                                        <SelectInput
                                                            label="Default Currency"
                                                            placeholder="Currency"
                                                            value={defaultCurrency}
                                                            onChange={(e) => setDefaultCurrency(e.target.value)}
                                                            items={(currenciesList || []).map(c => ({ value: c.code, key: `${c.code} - ${c.name}`, label: `${c.code} - ${c.name}` }))}
                                                            disabled={!writePermission}
                                                        />
                                                        {defaultCurrency && defaultCurrency !== "none" && defaultCurrency !== "" && (
                                                            <div className="mt-1 small italic-subtext text-secondary d-flex align-items-start currency-note-wrapper">

                                                                <span>
                                                                    Note: If default currency is different from your {getPlatformDisplayName(selectedConnection?.platform)} home currency, a default exchange rate of 1.0 will be applied unless overridden during sync.
                                                                </span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* 2. Line Item Aggregation Card */}
                            <Col xl={6} lg={12}>
                                <Card className="border shadow-sm h-100">
                                    <Card.Header className="bg-white py-3 px-3 border-bottom d-flex align-items-center">
                                        <h6 className="fw-bold text-uppercase m-0 small brand-heading">Line Item Aggregation</h6>
                                    </Card.Header>
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="pb-2 mb-3 border-bottom d-flex align-items-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    id="squash-both"
                                                    label={<span className="fw-bold text-dark ms-1">Squash line items to single row for both</span>}
                                                    checked={squashLinesInvoices && squashLinesBills}
                                                    disabled={!writePermission}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setSquashLinesInvoices(checked);
                                                        setSquashLinesBills(checked);
                                                        if (!checked) {
                                                            setSquashRetention(false);
                                                        }
                                                    }}
                                                    className="d-flex align-items-center m-0"
                                                />
                                            </div>
                                            <Row className="g-2 mb-2">
                                                <Col md={4} xs={12}>
                                                    <div className="p-2 border rounded bg-light-subtle h-100">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="squash-invoices"
                                                            label={<span className="text-dark ms-1 small fw-medium">Squash Invoices (AR)</span>}
                                                            checked={squashLinesInvoices}
                                                            disabled={!writePermission}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setSquashLinesInvoices(checked);
                                                                if (!checked && !squashLinesBills) {
                                                                    setSquashRetention(false);
                                                                }
                                                            }}
                                                            className="d-flex align-items-center m-0"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={4} xs={12}>
                                                    <div className="p-2 border rounded bg-light-subtle h-100">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="squash-bills"
                                                            label={<span className="text-dark ms-1 small fw-medium">Squash Bills (AP)</span>}
                                                            checked={squashLinesBills}
                                                            disabled={!writePermission}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setSquashLinesBills(checked);
                                                                if (!checked && !squashLinesInvoices) {
                                                                    setSquashRetention(false);
                                                                }
                                                            }}
                                                            className="d-flex align-items-center m-0"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={4} xs={12}>
                                                    <div className="p-2 border rounded bg-light-subtle h-100">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="squash-retention"
                                                            label={<span className="text-dark ms-1 small fw-bold">Squash Retention</span>}
                                                            checked={squashRetention}
                                                            onChange={(e) => setSquashRetention(e.target.checked)}
                                                            disabled={!writePermission || (!squashLinesInvoices && !squashLinesBills)}
                                                            className="d-flex align-items-center m-0"
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                        <span className="d-block mt-2 small italic-subtext">
                                            Choose whether to aggregate sales invoices (AR) and vendor bills (AP) into single summary rows. When enabled, withheld and released retention will be included in the squashed line item.
                                        </span>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Row 2: Bottom Pair (Left: Card 3 | Right: Card 4) */}
                        <Row className="g-4">
                            {/* 3. Revenue & Expense Accounts Card */}
                            <Col xl={6} lg={12}>
                                <Card className="border shadow-sm h-100">
                                    <Card.Header className="bg-white py-3 px-3 border-bottom d-flex align-items-center">
                                        <h6 className="fw-bold text-uppercase m-0 small brand-heading">Revenue & Expense Accounts</h6>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <Row className="g-3">
                                            <Col xs={12}>
                                                {loadingMetadata ? (
                                                    <div className="py-2 text-muted small align-with-inputs">
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Loading {selectedConnection?.platform === 'quickbooks' ? 'items' : 'accounts'}...
                                                    </div>
                                                ) : selectedConnection?.platform === 'quickbooks' ? (
                                                    <SelectInput
                                                        label="Product / Service"
                                                        placeholder="Product / Service"
                                                        value={defaultItemId}
                                                        onChange={(e) => setDefaultItemId(e.target.value)}
                                                        items={(itemsList || []).map(item => ({
                                                            value: item.code,
                                                            key: `${item.name}${item.taxable ? ' (Taxable)' : ' (Non-Taxable)'}`,
                                                            label: `${item.name}${item.taxable ? ' (Taxable)' : ' (Non-Taxable)'}`
                                                        }))}
                                                        disabled={!writePermission}
                                                    />
                                                ) : (
                                                    <SelectInput
                                                        label="Revenue Account"
                                                        placeholder="Revenue Account"
                                                        value={accountCode}
                                                        onChange={(e) => setAccountCode(e.target.value)}
                                                        items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}`, label: `${a.code} - ${a.name}` }))}
                                                        disabled={!writePermission}
                                                    />
                                                )}
                                            </Col>

                                            {['xero', 'quickbooks'].includes(selectedConnection?.platform) && (
                                                <>
                                                    <Col xs={12} sm={6}>
                                                        {loadingMetadata ? (
                                                            <div className="py-2 text-muted small align-with-inputs">
                                                                <Spinner size="sm" animation="border" className="me-2" />
                                                                Loading accounts...
                                                            </div>
                                                        ) : (
                                                            <SelectInput
                                                                label="Supplier Account Type (Class Filter)"
                                                                placeholder="Supplier Account Type"
                                                                value={cogsAccountType}
                                                                onChange={(e) => {
                                                                    setCogsAccountType(e.target.value);
                                                                    setCogsAccountCode('');
                                                                }}
                                                                disabled={!writePermission}
                                                                items={accountTypeOptions}
                                                            />
                                                        )}
                                                    </Col>
                                                    <Col xs={12} sm={6}>
                                                        {loadingMetadata ? (
                                                            <div className="py-2 text-muted small align-with-inputs">
                                                                <Spinner size="sm" animation="border" className="me-2" />
                                                                Loading accounts...
                                                            </div>
                                                        ) : (
                                                            <SelectInput
                                                                label="Contractor Account Type (Class Filter)"
                                                                placeholder="Contractor Account Type"
                                                                value={contractorAccountType}
                                                                onChange={(e) => {
                                                                    setContractorAccountType(e.target.value);
                                                                    setContractorAccountCode('');
                                                                }}
                                                                disabled={!writePermission}
                                                                items={accountTypeOptions}
                                                            />
                                                        )}
                                                    </Col>
                                                </>
                                            )}

                                            <Col xs={12} sm={6}>
                                                {loadingMetadata ? (
                                                    <div className="py-2 text-muted small align-with-inputs">
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Loading accounts...
                                                    </div>
                                                ) : (
                                                    <SelectInput
                                                        label="Supplier Account (COGS) *"
                                                        placeholder="COGS Account"
                                                        value={cogsAccountCode}
                                                        onChange={(e) => setCogsAccountCode(e.target.value)}
                                                        disabled={!writePermission}
                                                        items={(cogsAccountType
                                                            ? accountsList.filter(a => matchesAccountType(a.type, cogsAccountType))
                                                            : accountsList).map(a => ({ value: a.code, key: `${a.code} - ${a.name}`, label: `${a.code} - ${a.name}` }))}
                                                    />
                                                )}
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                {loadingMetadata ? (
                                                    <div className="py-2 text-muted small align-with-inputs">
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Loading accounts...
                                                    </div>
                                                ) : (
                                                    <SelectInput
                                                        label="Contractor Account (Subcontractors) *"
                                                        placeholder="Contractor Account"
                                                        value={contractorAccountCode}
                                                        onChange={(e) => setContractorAccountCode(e.target.value)}
                                                        disabled={!writePermission}
                                                        items={(contractorAccountType
                                                            ? accountsList.filter(a => matchesAccountType(a.type, contractorAccountType))
                                                            : accountsList).map(a => ({ value: a.code, key: `${a.code} - ${a.name}`, label: `${a.code} - ${a.name}` }))}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* 4. Tax & Tracking Configuration Card */}
                            <Col xl={6} lg={12}>
                                <Card className="border shadow-sm h-100">
                                    <Card.Header className="bg-white py-3 px-3 border-bottom d-flex align-items-center">
                                        <h6 className="fw-bold text-uppercase m-0 small brand-heading">Tax & Tracking Configuration</h6>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <Row className="g-3">
                                            <Col xs={12}>
                                                <SelectInput
                                                    label="Tax Calculation Mode"
                                                    placeholder="Select tax mode"
                                                    value={lineAmountTypes}
                                                    onChange={(e) => setLineAmountTypes(e.target.value)}
                                                    disabled={!writePermission}
                                                    items={[
                                                        { value: "Exclusive", key: "Tax Exclusive", label: "Tax Exclusive" },
                                                        { value: "Inclusive", key: "Tax Inclusive", label: "Tax Inclusive" }
                                                    ]}
                                                />
                                                <span className="d-block mt-1 small italic-subtext">
                                                    Platform-wide setting determining whether transaction amounts are sent to your accounting system with or without tax.
                                                </span>
                                            </Col>
                                            <Col xs={12} sm={selectedConnection?.platform === 'quickbooks' ? 12 : 6}>
                                                {loadingMetadata ? (
                                                    <div className="py-2 text-muted small align-with-inputs">
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Loading tax rates...
                                                    </div>
                                                ) : (
                                                    <SelectInput
                                                        label="Standard Sales Tax Type"
                                                        placeholder="Sales Tax Type"
                                                        value={taxType}
                                                        onChange={(e) => setTaxType(e.target.value)}
                                                        disabled={!writePermission}
                                                        items={taxRatesList.map(t => ({ value: t.code, key: `${t.name} (${t.rate || 0}%)`, label: `${t.name} (${t.rate || 0}%)` }))}
                                                    />
                                                )}
                                            </Col>
                                            {selectedConnection?.platform !== 'quickbooks' && (
                                                <Col xs={12} sm={6}>
                                                    {loadingMetadata ? (
                                                        <div className="py-2 text-muted small align-with-inputs">
                                                            <Spinner size="sm" animation="border" className="me-2" />
                                                            Loading tax rates...
                                                        </div>
                                                    ) : (
                                                        <SelectInput
                                                            label="Standard Expense Tax Type"
                                                            placeholder="Expense Tax Type"
                                                            value={expenseTaxType}
                                                            onChange={(e) => setExpenseTaxType(e.target.value)}
                                                            disabled={!writePermission}
                                                            items={taxRatesList.map(t => ({ value: t.code, key: `${t.name} (${t.rate || 0}%)`, label: `${t.name} (${t.rate || 0}%)` }))}
                                                        />
                                                    )}
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {selectedConnection?.is_syncing && (
                            <div className="alert alert-warning py-2 px-3 mb-3 small border-warning-subtle d-flex align-items-center mt-3">
                                <Spinner size="sm" animation="border" className="me-2 text-warning" />
                                <span><strong>Syncing in progress...</strong> Configuration changes and sync triggers are temporarily disabled.</span>
                            </div>
                        )}

                        <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mt-4 pt-3 border-top" ref={bottomElementRef}>
                            <Button variant="primary" className="btn-ch" type="submit" disabled={!writePermission || selectedConnection?.is_syncing === true} data-cy="save-configuration-btn">
                                Save Configuration
                            </Button>
                            <Button
                                variant="outline-info"
                                className="btn-ch"
                                onClick={handleSyncContacts}
                                disabled={!canSyncContacts || selectedConnection?.is_syncing === true}
                                data-cy="sync-contacts-btn"
                            >
                                Sync Contacts
                            </Button>
                            <Button
                                variant="outline-info"
                                className="btn-ch"
                                onClick={handleOpenSyncInvoices}
                                disabled={!canSyncInvoices || selectedConnection?.is_syncing === true || !isPushAllowed}
                                title={!isPushAllowed ? "Push operations are disabled for this platform under your account's subscription plan" : ""}
                                data-cy="sync-invoices-btn"
                            >
                                Sync Invoices
                            </Button>
                            <Button
                                variant="outline-info"
                                className="btn-ch"
                                onClick={handleOpenSyncBills}
                                disabled={!canSyncBills || selectedConnection?.is_syncing === true || !isPushAllowed}
                                title={!isPushAllowed ? "Push operations are disabled for this platform under your account's subscription plan" : ""}
                                data-cy="sync-bills-btn"
                            >
                                Sync Bills
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Col>
    );
}
